import React, { memo, useMemo } from 'react';
import { Tooltip } from '@cognitiv/cyprus-ui';
import { faCalendarWeek, faCog, faCogs, faCube, faCubes, faFolderOpen } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { selectProject } from 'ducks/project/selectors';
import { clearProject } from 'ducks/project/slices';
import { MenuItemProps } from 'ducks/types/prop-types';
import { useQuerySave } from 'hooks/useQuerySave';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { Link, useLocation } from 'react-router-dom';
import { handleSideMenu } from 'utils/helpers';

import cn from 'components/menu/Menu.module.scss';
const { PUBLIC_URL, REACT_APP_VERSION } = process.env;

const MenuBase = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const project = useAppSelector(selectProject);
  const { theme } = useSwitchTheme();

  const { getPageQueries, getJobsQuery } = useQuerySave({
    page: null,
    search: location.search,
  });

  const selected = useMemo(() => handleSideMenu(location.pathname), [location.pathname]);

  const handleOpenProjects = () => {
    dispatch(clearProject());
  };

  return (
    <div
      className={cn.menu}
      style={{
        backgroundColor: theme.navigation_background,
        borderRight: theme.navigation_border,
      }}
    >
      <Link className={cn.imageWrapper} to="/projects" onClick={handleOpenProjects}>
        <img className={cn.logo} src={`${PUBLIC_URL}/brain-light.svg`} alt="Andromeda" />
      </Link>

      <div className={cn.actions} style={{ borderTop: theme.navigation_border }}>
        {selected === 'projects' && <MenuItem to="/projects" tooltip="Projects" icon={faFolderOpen} page="projects" />}
        {selected !== 'projects' && project.project_id && (
          <>
            <MenuItem
              to={`/projects/${project.project_id}/jobs/all${getJobsQuery()}`}
              tooltip="Jobs"
              icon={faCube}
              page="jobs"
              selected={selected}
            />
            <MenuItem
              to={`/projects/${project.project_id}/batches/all${getPageQueries('batches')}`}
              tooltip="Batches"
              icon={faCubes}
              page="batches"
              selected={selected}
            />
            <MenuItem
              to={`/projects/${project.project_id}/job_definitions/all${getPageQueries('job_definitions')}`}
              tooltip="Job Definitions"
              icon={faCog}
              page="job_definitions"
              selected={selected}
            />
            <MenuItem
              to={`/projects/${project.project_id}/batch_definitions/all${getPageQueries('batch_definitions')}`}
              tooltip="Batch Definitions"
              icon={faCogs}
              page="batch_definitions"
              selected={selected}
            />
            <MenuItem
              to={`/projects/${project.project_id}/scheduled_batches/all${getPageQueries('scheduled_batches')}`}
              tooltip="Scheduled Batches"
              icon={faCalendarWeek}
              page="scheduled_batches"
              selected={selected}
            />
          </>
        )}
      </div>
      <div className={cn.versionWrapper}>v{REACT_APP_VERSION}</div>
    </div>
  );
};

export const Menu = memo(MenuBase);

const MenuItem = memo(({ to, icon, selected, page, tooltip }: MenuItemProps) => {
  const { theme } = useSwitchTheme();

  return (
    <Link className={classNames(cn.item, { [cn.active]: selected === page || false })} to={to}>
      <FontAwesomeIcon icon={icon} />
      {tooltip && (
        <Tooltip theme={theme.tooltip} tooltip={tooltip} position={{ left: 60 }} width="max-content" caret="left" />
      )}
    </Link>
  );
});
